/**
 * Making a calendar, with the invitation gate in front of it.
 *
 * One component for every entry point — the home page's dialog, the landing
 * page, whatever comes next — because the question "does this visitor need a
 * code?" has one answer and it comes from `/api/config` plus who they are.
 *
 * It asks for a code only when one is needed: an open deployment, or anyone
 * signed in — who is past the gate whether or not they have a calendar yet —
 * sees a name and a button.
 */
import type { Calendar } from '@circle/shared';
import { normaliseInviteCode } from '@circle/shared';
import { ApiError, api } from '../app/api';
import { invitesRequired, loadAppConfig } from '../app/auth';
import { ownerLink, ownerTokens } from '../app/owner-tokens';
import { openDialog } from './dialog';
import { button, copyRow, el, setChildren, textField, withBusy } from './dom';
import { errorMessage } from './toast';

/**
 * Where `/i/<code>` leaves the code for whichever page renders next.
 *
 * sessionStorage, not the URL: the code is spent by the create request, and a
 * link that keeps working after the tab is closed is not a thing to leave in an
 * address bar someone screenshots.
 */
export const INVITE_STORAGE_KEY = 'circle:invite';

function session(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function rememberInviteCode(code: string): void {
  try {
    session()?.setItem(INVITE_STORAGE_KEY, code);
  } catch {
    /* private mode: the visitor can still paste it */
  }
}

/** Reads the pending code and consumes it, so a later create does not reuse it. */
export function takeInviteCode(): string | null {
  const store = session();
  try {
    const code = store?.getItem(INVITE_STORAGE_KEY) ?? null;
    if (code) store?.removeItem(INVITE_STORAGE_KEY);
    return code;
  } catch {
    return null;
  }
}

export interface InviteCreateOptions {
  /** Fires once the whole flow is done — the owner link has been shown and dismissed. */
  onCreated?: (calendar: Calendar) => void;
  /** Fires the moment the calendar exists. Dismiss any host dialog here. */
  beforeOwnerLink?: (calendar: Calendar) => void;
  nameLabel?: string;
  submitLabel?: string;
  /** Put the cursor in the first empty field once it is known which that is. */
  autofocus?: boolean;
}

export interface InviteCreateHandle {
  destroy(): void;
  /** Resolves when the form has finished asking the server who is here. */
  ready: Promise<void>;
}

const GATE_CODES = new Set(['INVITE_REQUIRED', 'INVITE_INVALID']);

export function mountInviteCreate(
  container: HTMLElement,
  options: InviteCreateOptions = {},
): InviteCreateHandle {
  const name = textField({
    label: options.nameLabel ?? 'What is this ring for?',
    name: 'name',
    placeholder: 'Family and friends',
    maxlength: '80',
    required: true,
  });

  const code = textField({
    label: 'Invitation code',
    name: 'invite',
    placeholder: 'circle-XXXX-XXXX',
    maxlength: '200',
    autocapitalize: 'off',
    autocorrect: 'off',
    spellcheck: 'false',
    hint: 'Paste the code you were sent, or the whole link.',
  });

  const create = button(
    options.submitLabel ?? 'Create',
    () => void withBusy(create, submit),
    { class: 'btn--primary' },
  );

  /** Hidden until the deployment says a code is needed, or one was handed in. */
  let gated = false;
  let destroyed = false;

  const paint = (): void => {
    setChildren(container, [gated ? code.root : null, name.root, el('div', { class: 'row' }, create)]);
  };

  const submitOnEnter = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    create.click();
  };
  name.input.addEventListener('keydown', submitOnEnter);
  code.input.addEventListener('keydown', submitOnEnter);

  async function submit(): Promise<void> {
    const value = name.input.value.trim();
    if (!value) {
      name.setError('Give it a name.');
      return;
    }
    name.setError(null);

    const typed = code.input.value.trim();
    if (gated && typed && !normaliseInviteCode(typed)) {
      // Catch the typo here rather than spending one of the attempts the
      // worker allows per hour on it.
      code.setError('That does not look like a code. They read circle-XXXX-XXXX.');
      return;
    }
    code.setError(null);

    try {
      const calendar = await api.createCalendar(value, typed || undefined);
      if (calendar.ownerToken) ownerTokens.set(calendar.id, calendar.ownerToken);
      options.beforeOwnerLink?.(calendar);
      showOwnerLink(calendar, () => options.onCreated?.(calendar));
    } catch (err) {
      // The server is the authority on the gate: if it asks for a code the
      // form was not showing, show it and put its sentence under the box.
      if (err instanceof ApiError && GATE_CODES.has(err.code)) {
        if (!gated) {
          gated = true;
          paint();
        }
        code.setError(errorMessage(err, 'That invitation code was not accepted.'));
        code.input.focus();
        return;
      }
      name.setError(errorMessage(err, 'The calendar was not created.'));
    }
  }

  paint();

  const ready = (async () => {
    const pending = takeInviteCode();
    if (pending) code.input.value = pending;

    await loadAppConfig();
    // Anyone signed in is past the gate already and is not asked again — an
    // account is the thing a code buys, so having one is the whole test. The
    // form used to ask whether they already owned a calendar, which turned
    // away every signed-in person making their first one.
    const me = invitesRequired() ? await api.me().catch(() => null) : null;
    if (destroyed) return;

    gated = Boolean(pending) || (invitesRequired() && !me);
    paint();
    if (options.autofocus) (gated && !pending ? code.input : name.input).focus();
  })();

  return {
    ready,
    destroy() {
      destroyed = true;
      container.replaceChildren();
    },
  };
}

/**
 * The owner link, once. It is the calendar's only key until someone claims it,
 * and this is the only time it is shown.
 */
export function showOwnerLink(calendar: Calendar, onClose: () => void): void {
  const link = calendar.ownerToken ? ownerLink(calendar.id, calendar.ownerToken) : '';
  const handle = openDialog({
    title: 'This link is your key',
    description:
      'It is saved in this browser, so you can just carry on. Keep a copy somewhere safe: without it, and without an account, there is no way back into this calendar.',
    dismissable: false,
    body: [
      link ? copyRow(link, 'Copy link') : el('div', { class: 'empty', text: 'No link was returned.' }),
      el('div', {
        class: 'field__hint',
        text: 'You can attach the calendar to a passkey later, from Keys.',
      }),
    ],
    onClose,
  });
  const go = button('I have saved it', () => handle.close(), { class: 'btn--primary' });
  handle.foot.append(el('span', { class: 'grow' }), go);
  handle.root.appendChild(handle.foot);
  go.focus();
}
